
import { Component, Prop, Vue } from 'vue-property-decorator';

// TypeScript interfaces for suggestion data
interface QuestionSuggestion {
  question: string;              // The suggested question text
  category?: string;             // Category (e.g., 'Performance', 'Trends', 'Alerts')
  complexity?: 'simple' | 'moderate' | 'complex';  // Complexity level
  relevance_score?: number;      // Score 0-1 indicating relevance
  reason?: string;               // Why this suggestion is relevant
  context?: string;              // Additional context for the suggestion
  related_columns?: string[];    // Data columns this question will use
  estimated_processing_time?: number;  // Estimated seconds to process
  source?: 'ai_generated' | 'template' | 'fallback';  // Source of suggestion
  instruction?: string; // New field for instruction
}

@Component
export default class QuestionSuggestionCard extends Vue {
  @Prop({ required: true }) suggestion!: QuestionSuggestion;
  @Prop({ default: false }) showMetrics!: boolean;
  @Prop({ default: false }) showHoverActions!: boolean;
  @Prop({ default: false }) expandable!: boolean;
  @Prop({ default: false }) isLoading!: boolean;

  // Component state
  public showDetails: boolean = false;

  // Computed properties
  get shouldShowActions(): boolean {
    return this.showHoverActions || this.expandable;
  }

  // Event handlers
  public handleSuggestionClick(): void {
    if (this.isLoading) return;
    
    this.$emit('suggestion-selected', {
      suggestion: this.suggestion,
      question: this.suggestion.question
    });
  }

  public toggleDetails(): void {
    if (this.expandable) {
      this.showDetails = !this.showDetails;
      this.$emit('suggestion-toggled', {
        suggestion: this.suggestion,
        expanded: this.showDetails
      });
    }
  }

  // Styling methods
  public getSuggestionCardClass(): string {
    const classes = ['suggestion-card'];
    
    if (this.suggestion.complexity) {
      classes.push(`suggestion-card--${this.suggestion.complexity}`);
    }
    
    if (this.suggestion.relevance_score && this.suggestion.relevance_score >= 0.8) {
      classes.push('suggestion-card--high-relevance');
    }
    
    if (this.isLoading) {
      classes.push('suggestion-card--loading');
    }
    
    return classes.join(' ');
  }

  public getCategoryColor(): string {
    const category = (this.suggestion.category || '').toLowerCase();
    
    const categoryColors: { [key: string]: string } = {
      'performance': 'primary',
      'trends': 'info', 
      'alerts': 'warning',
      'optimization': 'success',
      'analysis': 'purple',
      'comparison': 'orange',
      'forecasting': 'teal',
      'general': 'grey'
    };
    
    return categoryColors[category] || 'grey';
  }

  public getCategoryIcon(): string {
    const category = (this.suggestion.category || '').toLowerCase();
    
    const categoryIcons: { [key: string]: string } = {
      'performance': '📊',
      'trends': '📈',
      'alerts': '⚠️',
      'optimization': '🎯',
      'analysis': '🔍',
      'comparison': '⚖️',
      'forecasting': '🔮',
      'general': '💡'
    };
    
    return categoryIcons[category] || '💡';
  }

  public getRelevanceColor(): string {
    const score = this.suggestion.relevance_score || 0;
    
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'error';
  }

  public getComplexityColor(): string {
    const complexity = this.suggestion.complexity;
    
    const complexityColors: { [key: string]: string } = {
      'simple': 'success',
      'moderate': 'warning',
      'complex': 'error'
    };
    
    return complexityColors[complexity || 'simple'] || 'grey';
  }

  public getComplexityLabel(): string {
    const complexity = this.suggestion.complexity;
    
    const complexityLabels: { [key: string]: string } = {
      'simple': 'Simple',
      'moderate': 'Moderate',
      'complex': 'Complex'
    };
    
    return complexityLabels[complexity || 'simple'] || 'Simple';
  }
}
